/**
 * Cross-game visual filter stack.
 *
 * Implements 5 toggleable post-processing filters (CRT, Old Film,
 * Pixelate, Tilt-Shift, God Rays) as Pixi v8 `Filter` subclasses with
 * inline GLSL ES 3.0 shaders, plus a {@link FilterPipeline} helper that
 * a scene attaches to its root container, ticks every frame, and
 * destroys on teardown.
 *
 * Each filter is an opt-in stylistic effect — the defaults in
 * `data/shared.config.js` are all `false`. The filters are designed to
 * stack cleanly in order (Pixelate first → Tilt-Shift → God Rays →
 * CRT → Old Film) so combining them looks intentional rather than
 * crushed; the pipeline applies them in that fixed order regardless of
 * which subset is enabled.
 *
 * IMPORTANT: shaders are WebGL only (Pixi default backend). If the app
 * is initialised with `preference: 'webgpu'` the per-filter constructor
 * falls back to a no-op via the {@link _safeFilter} guard so the game
 * never crashes when running on a renderer we don't have a WGSL shader
 * for.
 */

import { Filter, GlProgram, Rectangle } from '../../vendor/pixi.min.mjs';

// ── shared filter vertex shader ───────────────────────────────────────
//
// Pulled verbatim from Pixi v8's bundled `defaultFilterVertex` so our
// custom filters stay binary-compatible with the global filter
// uniforms (`uInputSize`, `uOutputFrame`, `uOutputTexture`) the
// renderer fills in for every filter pass.
//
// IMPORTANT: every fragment shader below MUST start with
// `precision highp float;` (or declare `uInputSize` as `highp` directly)
// — without it GLSL ES 3.0 defaults the fragment-stage `uInputSize` to
// `mediump`, mismatching the vertex stage's `highp` default and the
// program fails to link with:
//   "Precisions of uniform 'uInputSize' differ between VERTEX and
//    FRAGMENT shaders."
const SHARED_VERTEX = /* glsl */`
in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void) {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`;

/**
 * Wrap a Filter constructor in a try/catch so a shader compile failure
 * (e.g. browser without WebGL2) returns null instead of throwing into
 * the scene init flow. The caller is expected to skip null filters.
 */
function _safeFilter(builder) {
    try { return builder(); } catch { return null; }
}

// ── 1. Pixelate ───────────────────────────────────────────────────────
//
// Snaps each fragment's UV to a coarse grid so the entire stage looks
// like blown-up pixel art. The grid size is configurable but the
// default (~6 px) is the sweet spot where the play arena still reads
// clearly without losing readable text on the HUD.
class _PixelateFilter extends Filter {
    constructor(opts = {}) {
        const pixelSize = Number.isFinite(opts.size) ? opts.size : 6;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform float uPixelSize;
void main() {
    vec2 inv = uInputSize.zw;
    vec2 size = vec2(uPixelSize) * inv;
    vec2 uv = floor(vTextureCoord / size + 0.5) * size;
    finalColor = texture(uTexture, uv);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'pixelate' }),
            resources: { pixelateUniforms: { uPixelSize: { value: pixelSize, type: 'f32' } } },
        });
        this._uniforms = this.resources.pixelateUniforms.uniforms;
    }
    update(/* delta */) { /* no animation */ }
    setSize(px) { this._uniforms.uPixelSize = px; }
}

// ── 2. CRT ────────────────────────────────────────────────────────────
//
// Cathode-ray-tube look: subtle barrel curvature, scanlines (every
// other line dimmed), chromatic aberration (RGB channels offset
// horizontally), and a soft vignette. `uTime` drives a slow vertical
// roll so the scanlines shift like a real un-locked CRT. Tuned so the
// arena stays fully playable — the effect should feel like a filter,
// not a barrier.
class _CRTFilter extends Filter {
    constructor(opts = {}) {
        const curvature     = Number.isFinite(opts.curvature)     ? opts.curvature     : 0.06;
        const scanline      = Number.isFinite(opts.scanline)      ? opts.scanline      : 0.55;
        const chroma        = Number.isFinite(opts.chroma)        ? opts.chroma        : 1.4;
        const vignette      = Number.isFinite(opts.vignette)      ? opts.vignette      : 0.45;
        const lineWidth     = Number.isFinite(opts.lineWidth)     ? opts.lineWidth     : 1.4;
        const noise         = Number.isFinite(opts.noise)         ? opts.noise         : 0.0;
        const noiseSize     = Number.isFinite(opts.noiseSize)     ? opts.noiseSize     : 1.0;
        const vignetteAlpha = Number.isFinite(opts.vignetteAlpha) ? opts.vignetteAlpha : 1.0;
        const seed          = Number.isFinite(opts.seed)          ? opts.seed          : 0.5;
        const vertical      = !!opts.verticalLine;
        const animating     = opts.animating === false ? false : true;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform float uTime;
uniform float uCurvature;
uniform float uScanlineIntensity;
uniform float uChromaShift;
uniform float uVignette;
uniform float uLineWidth;
uniform float uNoise;
uniform float uNoiseSize;
uniform float uVignetteAlpha;
uniform float uVertical;
uniform float uSeed;

vec2 curve(vec2 uv) {
    vec2 c = uv * 2.0 - 1.0;
    vec2 off = c.yx * c.yx * uCurvature;
    return (c + c * off) * 0.5 + 0.5;
}
float rand(vec2 p) {
    return fract(sin(dot(p, vec2(12.9898, 78.233)) + uSeed * 57.31) * 43758.5453);
}

void main() {
    vec2 uv = curve(vTextureCoord);
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
        finalColor = vec4(0.0, 0.0, 0.0, 1.0);
        return;
    }
    float shift = uChromaShift * uInputSize.z;
    float r = texture(uTexture, uv + vec2(shift, 0.0)).r;
    float g = texture(uTexture, uv).g;
    float b = texture(uTexture, uv - vec2(shift, 0.0)).b;
    float a = texture(uTexture, uv).a;
    vec3 col = vec3(r, g, b);

    // Scan lines on the chosen axis. uLineWidth scales the period —
    // smaller = denser lines, larger = bigger gaps. The 1.4 default
    // matches the original tuning (~1.4 px per line on a 1080p stage).
    float axis = mix(uv.y * uInputSize.y, uv.x * uInputSize.x, uVertical);
    float scan = sin(axis / max(uLineWidth, 0.0001) + uTime * 1.2);
    col *= 1.0 - uScanlineIntensity * (0.5 + 0.5 * scan) * 0.5;

    // Per-frame noise grain — sized in source pixels via uNoiseSize so
    // bigger values look like coarse VHS dropouts instead of fine
    // sensor noise.
    if (uNoise > 0.0) {
        vec2 npx = floor(vTextureCoord * uInputSize.xy / max(uNoiseSize, 0.5));
        float n = rand(npx + uTime);
        col += (n - 0.5) * uNoise;
    }

    // Vignette darkening, gated by uVignetteAlpha (0 = no vignette,
    // 1 = full strength). Lets the user keep a curved CRT shape but
    // drop the corner darkening if it crushes the gameplay corners.
    vec2 vc = uv - 0.5;
    float vig = 1.0 - dot(vc, vc) * uVignette;
    col = mix(col, col * clamp(vig, 0.0, 1.0), clamp(uVignetteAlpha, 0.0, 1.0));

    col *= 1.06;
    finalColor = vec4(col, a);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'crt' }),
            resources: {
                crtUniforms: {
                    uTime:              { value: 0,             type: 'f32' },
                    uCurvature:         { value: curvature,     type: 'f32' },
                    uScanlineIntensity: { value: scanline,      type: 'f32' },
                    uChromaShift:       { value: chroma,        type: 'f32' },
                    uVignette:          { value: vignette,      type: 'f32' },
                    uLineWidth:         { value: lineWidth,     type: 'f32' },
                    uNoise:             { value: noise,         type: 'f32' },
                    uNoiseSize:         { value: noiseSize,     type: 'f32' },
                    uVignetteAlpha:     { value: vignetteAlpha, type: 'f32' },
                    uVertical:          { value: vertical ? 1.0 : 0.0, type: 'f32' },
                    uSeed:              { value: seed,          type: 'f32' },
                },
            },
        });
        this._uniforms = this.resources.crtUniforms.uniforms;
        this._animating = animating;
    }
    update(delta) {
        // Freeze scanline roll + noise reshuffle when animating is off
        // so the user can dial in a static "still photograph" CRT
        // look (handy for screenshots / kiosk mode).
        if (this._animating) this._uniforms.uTime += delta * 0.05;
    }
}

// ── 3. Old Film ──────────────────────────────────────────────────────
//
// Sepia tint + animated film-grain noise + soft vignette + (very)
// sparse, faint vertical scratch lines. `uTime` advances each frame
// so the noise & scratches never look static. Picture-frame stays
// sharp; this filter is purely chromatic + noise so it composes
// nicely with CRT (CRT applies first, then sepia-tints the result).
//
// SCRATCH TUNING — earlier builds had a `uScratch ≈ 0.7` with a
// step-threshold of `0.985` (≈ 1.5 % of x-positions). On a high-
// resolution playfield that read as bright vertical "fence posts"
// flickering across the arena every few frames — too aggressive for
// a subtle film-stock vibe. The current tuning:
//
//   • threshold raised to 0.997 (≈ 0.3 % of columns get a scratch)
//   • intensity dropped to 0.18 and BLENDED via mix() instead of
//     additive saturation — keeps gameplay clearly readable through
//     the line and stops the bright tinted bands you saw earlier
//   • a soft sin() falloff along Y so scratches fade in/out vertically
//     rather than running edge-to-edge as solid pillars
class _OldFilmFilter extends Filter {
    constructor(opts = {}) {
        const sepia     = Number.isFinite(opts.sepia)    ? opts.sepia    : 0.85;
        const noise     = Number.isFinite(opts.noise)    ? opts.noise    : 0.18;
        const scratch   = Number.isFinite(opts.scratch)  ? opts.scratch  : 0.18;
        const vignette  = Number.isFinite(opts.vignette) ? opts.vignette : 0.55;
        const animating = opts.animating === false ? false : true;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform float uTime;
uniform float uSepia;
uniform float uNoise;
uniform float uScratch;
uniform float uVignette;

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
    vec2 uv = vTextureCoord;
    vec4 src = texture(uTexture, uv);
    vec3 col = src.rgb;
    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    vec3 sepia = vec3(lum * 1.07, lum * 0.82, lum * 0.55);
    col = mix(col, sepia, uSepia);
    float n = hash(uv * vec2(uInputSize.x, uInputSize.y) + uTime * 9.7);
    col += (n - 0.5) * uNoise;
    float scratchSeed = floor(uTime * 6.0);
    float sx = hash(vec2(scratchSeed, 1.0));
    float scratchPick = hash(vec2(uv.x * 180.0 + sx * 180.0, scratchSeed));
    float scratchMask = step(0.997, scratchPick);
    float yFade = 0.4 + 0.6 * sin(uv.y * 2.7 + scratchSeed * 1.7);
    float scratchAlpha = scratchMask * uScratch * yFade;
    col = mix(col, vec3(0.92, 0.85, 0.66), clamp(scratchAlpha, 0.0, 0.6));
    vec2 vc = uv - 0.5;
    float vig = 1.0 - dot(vc, vc) * uVignette;
    col *= clamp(vig, 0.0, 1.0);
    finalColor = vec4(col, src.a);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'oldfilm' }),
            resources: {
                oldFilmUniforms: {
                    uTime:     { value: 0,        type: 'f32' },
                    uSepia:    { value: sepia,    type: 'f32' },
                    uNoise:    { value: noise,    type: 'f32' },
                    uScratch:  { value: scratch,  type: 'f32' },
                    uVignette: { value: vignette, type: 'f32' },
                },
            },
        });
        this._uniforms = this.resources.oldFilmUniforms.uniforms;
        this._animating = animating;
    }
    update(delta) {
        // When animating is off, both the noise grain and the
        // scratch-line picker freeze on whatever frame they were on
        // (uTime determines both via hash() inside the shader).
        if (this._animating) this._uniforms.uTime += delta * 0.016;
    }
}

// ── 4. Tilt-Shift ─────────────────────────────────────────────────────
//
// Real tilt-shift photography places the lens's focal plane along a
// single horizontal slice of the frame — everything on that line is
// sharp left-to-right and the image softens gradually as you move
// up or down. We mimic that here instead of doing a radial disc so
// the player's whole row reads cleanly (you can still see incoming
// vehicles / pickups beside the balloon, just like the snake head's
// row stays sharp end-to-end).
//
// Distance is computed along Y only:
//   • `uFocusBand`    — half-height of the perfectly sharp band (UV).
//   • `uFocusRange`   — extra Y distance over which the blur ramps
//                       quadratically from sharp → max.
//   • `uMaxBlur`      — peak blur radius in pixels at the top/bottom
//                       of the frame.
//
// The 5×5 box-blur taps are biased vertically (a wider Y stride than
// X) so the softening matches the direction of the falloff while
// still anti-aliasing horizontal edges (lane lines, vehicle rims).
//
// {@link setFocus} is called every frame by {@link FilterPipeline}.
// We only consume the Y coordinate; the X is accepted for API
// compatibility with the previous radial filter but ignored.
class _TiltShiftFilter extends Filter {
    constructor(opts = {}) {
        const band  = Number.isFinite(opts.band)  ? opts.band  : 0.18;
        const range = Number.isFinite(opts.range) ? opts.range : 0.50;
        const blur  = Number.isFinite(opts.blur)  ? opts.blur  : 7.0;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform float uFocusY;
uniform float uFocusBand;
uniform float uFocusRange;
uniform float uMaxBlur;

void main() {
    vec2 uv = vTextureCoord;
    float dy = abs(uv.y - uFocusY);
    float t = clamp((dy - uFocusBand) / max(0.0001, uFocusRange), 0.0, 1.0);
    float radius = t * t * uMaxBlur;
    if (radius < 0.001) {
        finalColor = texture(uTexture, uv);
        return;
    }
    vec2 step = uInputSize.zw * radius;
    vec4 acc = vec4(0.0);
    float total = 0.0;
    for (int i = -2; i <= 2; i++) {
        for (int j = -2; j <= 2; j++) {
            vec2 off = vec2(float(i) * 0.6, float(j) * 1.4) * step;
            acc += texture(uTexture, uv + off);
            total += 1.0;
        }
    }
    finalColor = acc / total;
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'tiltshift' }),
            resources: {
                tiltShiftUniforms: {
                    // Defaults: sharp band ~36% of screen height,
                    // gentle quadratic ramp over the next 50%, peak
                    // blur 7 px (was 18 px and radial). Tuned so the
                    // gameplay row stays legible while the far edges
                    // still pick up a tilt-shift "miniature" feel.
                    uFocusY:     { value: 0.5,   type: 'f32' },
                    uFocusBand:  { value: band,  type: 'f32' },
                    uFocusRange: { value: range, type: 'f32' },
                    uMaxBlur:    { value: blur,  type: 'f32' },
                },
            },
        });
        this._uniforms = this.resources.tiltShiftUniforms.uniforms;
    }
    update(/* delta */) { /* no animation — focus tracked externally via setFocus */ }
    /**
     * Move the in-focus horizontal band. Only the Y coordinate is
     * consumed (the filter always keeps a full-width sharp slice);
     * the X is accepted for API compatibility with the previous
     * radial variant and intentionally ignored.
     */
    setFocus(_x, y) {
        if (Number.isFinite(y)) this._uniforms.uFocusY = y;
    }
}

// ── 5. God Rays ───────────────────────────────────────────────────────
//
// Volumetric light shafts radiating from a high-up off-screen sun
// (default upper-right). Built from Worley-style noise sampled along
// the ray from the sun to the current pixel, attenuated by distance
// and softly added back to the underlying scene. `uTime` advances
// continuously so the ray pattern slowly drifts.
class _GodRaysFilter extends Filter {
    constructor(opts = {}) {
        const intensity = Number.isFinite(opts.intensity) ? opts.intensity : 0.6;
        const exposure  = Number.isFinite(opts.exposure)  ? opts.exposure  : 0.50;
        const decay     = Number.isFinite(opts.decay)     ? opts.decay     : 0.965;
        const lightX    = Number.isFinite(opts.lightX)    ? opts.lightX    : 0.85;
        const lightY    = Number.isFinite(opts.lightY)    ? opts.lightY    : -0.05;
        const animating = opts.animating === false ? false : true;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform float uTime;
uniform vec2 uLightPos;
uniform float uIntensity;
uniform float uDecay;
uniform float uExposure;
uniform vec3 uColor;

float n2(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(n2(i + vec2(0.0, 0.0)), n2(i + vec2(1.0, 0.0)), u.x),
               mix(n2(i + vec2(0.0, 1.0)), n2(i + vec2(1.0, 1.0)), u.x), u.y);
}

void main() {
    vec2 uv = vTextureCoord;
    vec4 src = texture(uTexture, uv);
    vec2 toLight = uLightPos - uv;
    float dist = length(toLight);
    vec2 step = toLight / 40.0;
    vec2 sample = uv;
    float illum = 0.0;
    float decay = 1.0;
    for (int i = 0; i < 40; i++) {
        sample += step;
        float angle = atan(sample.y - uLightPos.y, sample.x - uLightPos.x);
        float band = noise(vec2(angle * 6.0, uTime * 0.3));
        illum += band * decay;
        decay *= uDecay;
    }
    illum *= uExposure / 40.0;
    float falloff = clamp(1.0 - dist * 0.9, 0.0, 1.0);
    vec3 add = uColor * illum * falloff * uIntensity;
    finalColor = vec4(src.rgb + add, src.a);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'godrays' }),
            resources: {
                godRaysUniforms: {
                    // Intensity was 0.9 and read as a hard yellow
                    // wash on bright backgrounds (frogger's road,
                    // snake's free-roam fields). Defaults dropped to
                    // 0.6 / exposure 0.5 — the rays still sweep
                    // across the corner but no longer flatten
                    // gameplay. All five values can now be overridden
                    // live via the Visual Filters settings panel.
                    uTime:      { value: 0,                                       type: 'f32' },
                    uLightPos:  { value: new Float32Array([lightX, lightY]),      type: 'vec2<f32>' },
                    uIntensity: { value: intensity,                               type: 'f32' },
                    uDecay:     { value: decay,                                   type: 'f32' },
                    uExposure:  { value: exposure,                                type: 'f32' },
                    uColor:     { value: new Float32Array([1.0, 0.93, 0.78]),     type: 'vec3<f32>' },
                },
            },
        });
        this._uniforms = this.resources.godRaysUniforms.uniforms;
        this._animating = animating;
    }
    update(delta) {
        // Freezing locks the ray noise pattern, useful for static
        // backdrop captures.
        if (this._animating) this._uniforms.uTime += delta * 0.02;
    }
}

// ── 6. Adjustment ─────────────────────────────────────────────────────
//
// Colour-grading panel: gamma → saturation → contrast → brightness →
// per-channel multipliers. All defaults are 1.0 so the filter is an
// identity transform until the user touches a slider — handy for
// nudging a build's tone without recolouring every asset.
class _AdjustmentFilter extends Filter {
    constructor(opts = {}) {
        const gamma      = Number.isFinite(opts.gamma)      ? opts.gamma      : 1.0;
        const saturation = Number.isFinite(opts.saturation) ? opts.saturation : 1.0;
        const contrast   = Number.isFinite(opts.contrast)   ? opts.contrast   : 1.0;
        const brightness = Number.isFinite(opts.brightness) ? opts.brightness : 1.0;
        const red        = Number.isFinite(opts.red)        ? opts.red        : 1.0;
        const green      = Number.isFinite(opts.green)      ? opts.green      : 1.0;
        const blue       = Number.isFinite(opts.blue)       ? opts.blue       : 1.0;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform float uGamma;
uniform float uSaturation;
uniform float uContrast;
uniform float uBrightness;
uniform float uRed;
uniform float uGreen;
uniform float uBlue;

void main() {
    vec4 c = texture(uTexture, vTextureCoord);
    vec3 col = c.rgb;
    col = pow(max(col, vec3(0.0)), vec3(1.0 / max(uGamma, 0.0001)));
    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(lum), col, uSaturation);
    col = (col - 0.5) * uContrast + 0.5;
    col *= uBrightness;
    col.r *= uRed;
    col.g *= uGreen;
    col.b *= uBlue;
    finalColor = vec4(col, c.a);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'adjust' }),
            resources: {
                adjustUniforms: {
                    uGamma:      { value: gamma,      type: 'f32' },
                    uSaturation: { value: saturation, type: 'f32' },
                    uContrast:   { value: contrast,   type: 'f32' },
                    uBrightness: { value: brightness, type: 'f32' },
                    uRed:        { value: red,        type: 'f32' },
                    uGreen:      { value: green,      type: 'f32' },
                    uBlue:       { value: blue,       type: 'f32' },
                },
            },
        });
    }
    update(/* delta */) { /* no animation */ }
}

// ── 7. Reflection ─────────────────────────────────────────────────────
//
// Mirrors the upper portion of the frame into the lower portion with
// an animated horizontal sine-wave displacement and a depth-fade
// alpha — the classic "watery puddle" or "wet street" reflection.
//
//   • uBoundary    — vertical UV where the reflection band starts.
//   • uAmplitude   — peak horizontal displacement, in pixels (scaled
//                    by uInputSize.z internally so it stays consistent
//                    across viewport sizes).
//   • uWaveLength  — number of cycles down the reflected band.
//   • uReflectionAlpha — opacity multiplier at the boundary; the
//                        reflection fades to 0 at the bottom edge.
//   • uSpeed       — how fast `uTime` advances the wave.
//
// The boundary line itself is sharp on purpose; combine with
// Tilt-Shift (band aligned to the reflection edge) for a soft
// horizon if you want a gentler transition.
class _ReflectionFilter extends Filter {
    constructor(opts = {}) {
        const boundary  = Number.isFinite(opts.boundary)  ? opts.boundary  : 0.70;
        const amplitude = Number.isFinite(opts.amplitude) ? opts.amplitude : 8.0;
        const waveLen   = Number.isFinite(opts.waveLen)   ? opts.waveLen   : 8.0;
        const alpha     = Number.isFinite(opts.alpha)     ? opts.alpha     : 0.55;
        const speed     = Number.isFinite(opts.speed)     ? opts.speed     : 1.0;
        const animating = opts.animating === false ? false : true;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uInputSize;
uniform float uTime;
uniform float uBoundary;
uniform float uAmplitude;
uniform float uWaveLength;
uniform float uReflectionAlpha;
uniform float uSpeed;

void main() {
    vec2 uv = vTextureCoord;
    vec4 src = texture(uTexture, uv);
    if (uv.y <= uBoundary) {
        finalColor = src;
        return;
    }
    float depth = (uv.y - uBoundary) / max(0.0001, 1.0 - uBoundary);
    float waveX = sin(depth * uWaveLength * 6.28318 + uTime * uSpeed * 2.0)
                  * uAmplitude * uInputSize.z;
    float mirroredY = uBoundary - (uv.y - uBoundary);
    if (mirroredY < 0.0) {
        finalColor = src;
        return;
    }
    vec2 sampleUV = vec2(clamp(uv.x + waveX, 0.0, 1.0), mirroredY);
    vec4 reflected = texture(uTexture, sampleUV);
    float fade = (1.0 - depth) * uReflectionAlpha;
    finalColor = vec4(mix(src.rgb, reflected.rgb, fade), src.a);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'reflection' }),
            resources: {
                reflectionUniforms: {
                    uTime:             { value: 0,         type: 'f32' },
                    uBoundary:         { value: boundary,  type: 'f32' },
                    uAmplitude:        { value: amplitude, type: 'f32' },
                    uWaveLength:       { value: waveLen,   type: 'f32' },
                    uReflectionAlpha:  { value: alpha,     type: 'f32' },
                    uSpeed:            { value: speed,     type: 'f32' },
                },
            },
        });
        this._uniforms = this.resources.reflectionUniforms.uniforms;
        this._animating = animating;
    }
    update(delta) {
        // Pausing animation freezes the wave on whatever crest it
        // happened to be on — the boundary still shows a static
        // ripple, just no longer moving.
        if (this._animating) this._uniforms.uTime += delta * 0.02;
    }
}

// ── 8. Greyscale ──────────────────────────────────────────────────────
//
// Linear desaturation toward Rec.601 luminance with an optional
// per-channel tint applied AFTER desaturation — lets the user pull
// the result toward sepia, cyanotype, or any other monochromatic
// wash without juggling a separate tint filter. `uAmount = 0` is
// pass-through; `uAmount = 1` is full mono.
class _GreyscaleFilter extends Filter {
    constructor(opts = {}) {
        const amount = Number.isFinite(opts.amount) ? opts.amount : 1.0;
        const tintR  = Number.isFinite(opts.tintR)  ? opts.tintR  : 1.0;
        const tintG  = Number.isFinite(opts.tintG)  ? opts.tintG  : 1.0;
        const tintB  = Number.isFinite(opts.tintB)  ? opts.tintB  : 1.0;
        const fragment = /* glsl */`
precision highp float;
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform float uAmount;
uniform vec3 uTint;

void main() {
    vec4 c = texture(uTexture, vTextureCoord);
    float lum = dot(c.rgb, vec3(0.299, 0.587, 0.114));
    vec3 mono = vec3(lum) * uTint;
    finalColor = vec4(mix(c.rgb, mono, uAmount), c.a);
}`;
        super({
            glProgram: GlProgram.from({ vertex: SHARED_VERTEX, fragment, name: 'greyscale' }),
            resources: {
                greyscaleUniforms: {
                    uAmount: { value: amount, type: 'f32' },
                    uTint:   { value: new Float32Array([tintR, tintG, tintB]), type: 'vec3<f32>' },
                },
            },
        });
    }
    update(/* delta */) { /* no animation */ }
}

// ── public pipeline ───────────────────────────────────────────────────

/**
 * Reads the 5 cross-game filter toggles from settings and attaches the
 * matching filter chain to a target container. Pipeline order is
 * fixed (Pixelate → Tilt-Shift → God Rays → CRT → Old Film) so layered
 * combinations always look intentional.
 *
 * Usage:
 *   this._fx = new FilterPipeline(this.game, this.container);
 *   // in update(delta): this._fx.update(delta);
 *   // in destroy():     this._fx.destroy();
 */
export class FilterPipeline {
    /** @type {Filter[]} */
    _filters = [];
    /** @type {*} */
    _target = null;
    /** @type {*} */
    _prevFilters = null;
    /** @type {*} */
    _prevFilterArea = null;

    constructor(game, target) {
        if (!target) return;
        this._target = target;
        this._prevFilters = target.filters || null;
        this._prevFilterArea = target.filterArea || null;

        const getBool    = (k, def = false) => !!game?.theme?.getSetting?.('shared.settings', k, def);
        const getBoolDef = (k, def = true)  => {
            const v = game?.theme?.getSetting?.('shared.settings', k, def);
            return v === undefined || v === null ? def : !!v;
        };
        const getNum  = (k, def) => {
            const v = game?.theme?.getSetting?.('shared.settings', k, def);
            const n = Number(v);
            return Number.isFinite(n) ? n : def;
        };

        const flags = {
            pixelate:   getBool('pixelateFilter'),
            tiltShift:  getBool('tiltShiftFilter'),
            reflection: getBool('reflectionFilter'),
            godRays:    getBool('godRaysFilter'),
            adjustment: getBool('adjustmentFilter'),
            greyscale:  getBool('greyscaleFilter'),
            crt:        getBool('crtFilter'),
            oldFilm:    getBool('oldFilmFilter'),
        };

        // Per-filter customisation, read from the shared settings (see
        // SHARED_GAME.settings in `data/shared.config.js`). Defaults
        // here mirror the schema defaults so a missing/cleared key
        // still produces the original "factory" look.
        const opts = {
            pixelate: {
                size: getNum('pixelateSize', 6),
            },
            tiltShift: {
                band:  getNum('tiltShiftBand',  0.18),
                range: getNum('tiltShiftRange', 0.50),
                blur:  getNum('tiltShiftBlur',  7.0),
            },
            reflection: {
                animating: getBoolDef('reflectionAnimating', true),
                boundary:  getNum('reflectBoundary',   0.70),
                amplitude: getNum('reflectAmplitude',  8.0),
                waveLen:   getNum('reflectWaveLength', 8.0),
                alpha:     getNum('reflectAlpha',      0.55),
                speed:     getNum('reflectSpeed',      1.0),
            },
            godRays: {
                animating: getBoolDef('godRaysAnimating', true),
                intensity: getNum('godRaysIntensity', 0.6),
                exposure:  getNum('godRaysExposure',  0.5),
                decay:     getNum('godRaysDecay',     0.965),
                lightX:    getNum('godRaysLightX',    0.85),
                lightY:    getNum('godRaysLightY',   -0.05),
            },
            adjustment: {
                gamma:      getNum('adjustGamma',      1.0),
                saturation: getNum('adjustSaturation', 1.0),
                contrast:   getNum('adjustContrast',   1.0),
                brightness: getNum('adjustBrightness', 1.0),
                red:        getNum('adjustRed',        1.0),
                green:      getNum('adjustGreen',      1.0),
                blue:       getNum('adjustBlue',       1.0),
            },
            greyscale: {
                amount: getNum('greyscaleAmount', 1.0),
                tintR:  getNum('greyscaleTintR',  1.0),
                tintG:  getNum('greyscaleTintG',  1.0),
                tintB:  getNum('greyscaleTintB',  1.0),
            },
            crt: {
                animating:     getBoolDef('crtAnimating',    true),
                verticalLine:  getBool('crtVerticalLine',    false),
                curvature:     getNum('crtCurvature',        0.06),
                scanline:      getNum('crtScanline',         0.55),
                lineWidth:     getNum('crtLineWidth',        1.4),
                chroma:        getNum('crtChroma',           1.4),
                noise:         getNum('crtNoise',            0.0),
                noiseSize:     getNum('crtNoiseSize',        1.0),
                vignette:      getNum('crtVignette',         0.45),
                vignetteAlpha: getNum('crtVignetteAlpha',    1.0),
                seed:          getNum('crtSeed',             0.5),
            },
            oldFilm: {
                animating: getBoolDef('oldFilmAnimating', true),
                sepia:     getNum('oldFilmSepia',    0.85),
                noise:     getNum('oldFilmNoise',    0.18),
                scratch:   getNum('oldFilmScratch',  0.18),
                vignette:  getNum('oldFilmVignette', 0.55),
            },
        };

        // Pipeline order — chosen so each pass operates on the result
        // of the previous in a way that reads cleanly:
        //   Pixelate    — geometry snap (must run before any sampling
        //                  filter so blocks stay crisp).
        //   Tilt-Shift  — selective blur.
        //   Reflection  — geometric mirror (operates on already-blurred
        //                  pixels so the reflection inherits the focus
        //                  fall-off).
        //   God Rays    — additive lighting on top of the staged scene.
        //   Adjustment  — colour grading (after lighting so rays are
        //                  graded too).
        //   Greyscale   — desaturation last in the colour stack.
        //   CRT         — display tube (curvature + scanlines on top
        //                  of the graded image).
        //   Old Film    — film stock & sepia (final emulation pass).
        if (flags.pixelate)   this._push(_safeFilter(() => new _PixelateFilter(opts.pixelate)));
        if (flags.tiltShift)  this._push(_safeFilter(() => new _TiltShiftFilter(opts.tiltShift)));
        if (flags.reflection) this._push(_safeFilter(() => new _ReflectionFilter(opts.reflection)));
        if (flags.godRays)    this._push(_safeFilter(() => new _GodRaysFilter(opts.godRays)));
        if (flags.adjustment) this._push(_safeFilter(() => new _AdjustmentFilter(opts.adjustment)));
        if (flags.greyscale)  this._push(_safeFilter(() => new _GreyscaleFilter(opts.greyscale)));
        if (flags.crt)        this._push(_safeFilter(() => new _CRTFilter(opts.crt)));
        if (flags.oldFilm)    this._push(_safeFilter(() => new _OldFilmFilter(opts.oldFilm)));

        if (this._filters.length) {
            target.filters = this._filters;
            // Pin the filter framebuffer to the visible game viewport.
            // Without this, Pixi v8 sizes the framebuffer from the
            // container's natural bounds — which on scenes with deep
            // child trees, masked sub-containers, off-screen overlays,
            // or chrome attached at extreme coordinates can balloon
            // FAR beyond the visible area. That makes `uInputSize`
            // inside the CRT / Old Film / Pixelate shaders much bigger
            // than the on-screen size, so scanlines become a fine
            // moiré, pixelate snaps to a denser grid, and grain looks
            // like sandpaper. Locking `filterArea` to the canvas
            // resolves the inconsistency: every game now renders post-
            // FX at the same screen-space density.
            //
            // Additionally, every filter is forced to resolution=1 and
            // antialias='off' so the framebuffer that Pixi allocates is
            // EXACTLY filterArea.width × filterArea.height pixels. Pixi
            // v8's default is `resolution: 'inherit'` which picks the
            // renderer's resolution — and since we boot with
            // `resolution: 1` in Game.init() the practical effect is
            // already 1, but pinning it explicitly removes any
            // possibility that a downstream change (e.g. someone
            // re-enabling autoDensity for HiDPI) silently doubles the
            // filter texture and re-introduces the dense-scanline /
            // sandpaper-noise look on every game.
            try {
                const w = game?.width  || target.width  || 0;
                const h = game?.height || target.height || 0;
                if (w > 0 && h > 0) {
                    target.filterArea = new Rectangle(0, 0, w, h);
                }
            } catch { /* ok — leave filterArea unset on failure */ }
            for (const f of this._filters) {
                try {
                    f.resolution = 1;
                    f.antialias  = 'off';
                } catch { /* ok — older Pixi build */ }
            }
        }
    }

    _push(f) { if (f) this._filters.push(f); }

    /** Tick animated filters. Safe to call every frame even with none. */
    update(delta) {
        for (const f of this._filters) {
            if (typeof f.update === 'function') f.update(delta);
        }
    }

    /**
     * Forward a UV-space focus point to every filter that supports one
     * (currently just Tilt-Shift). Scenes call this per frame from
     * their `update(delta)` so the in-focus disc tracks the player —
     * without it the user can only see a fixed strip of the arena.
     *
     * @param {number} uvX — 0..1 (0 = left edge, 1 = right edge)
     * @param {number} uvY — 0..1 (0 = top  edge, 1 = bottom edge)
     */
    setFocus(uvX, uvY) {
        for (const f of this._filters) {
            if (typeof f.setFocus === 'function') f.setFocus(uvX, uvY);
        }
    }

    /**
     * Detach filters from the container and drop references. Restores
     * whatever filters the container had before the pipeline took over
     * (typically `null` — a {@link SnakeClouds} blur on a child
     * container is unaffected because we operate on the scene root).
     */
    destroy() {
        if (this._target) {
            try { this._target.filters = this._prevFilters; } catch { /* ok */ }
            // Restore whatever filterArea the container had before we
            // pinned it. Usually null, but if a scene set one of its
            // own before constructing the pipeline we want to leave
            // its original value intact.
            try { this._target.filterArea = this._prevFilterArea; } catch { /* ok */ }
        }
        for (const f of this._filters) {
            try { f.destroy?.(); } catch { /* ok */ }
        }
        this._filters.length = 0;
        this._target = null;
        this._prevFilters = null;
        this._prevFilterArea = null;
    }
}
